import React, { useState, useEffect } from 'react';
import api from '../../../../api';
import { FaUserCircle } from 'react-icons/fa';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import { useAuth } from '../../../Layouts/context/Authcontext';

const CardWrapper = ({ children }) => (
  <div className="relative w-full max-w-5xl mx-auto p-6 rounded-3xl overflow-hidden bg-white shadow-xl border border-gray-200">
    <div className="absolute bottom-0 left-6 right-6 h-1 bg-gradient-to-r from-[#EE1D23] to-[#1A2B5C] rounded-full opacity-40 blur-sm z-0" />
    <div className="relative">{children}</div>
  </div>
);

const NoDataMessage = () => (
  <div className="flex flex-col items-center justify-center h-full text-center animate-fadeIn">
    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-[#EE1D23]/70 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.75V3m0 18v-1.75M6.364 6.364l-1.061-1.061m12.728 12.728l-1.061-1.061M4.75 12H3m18 0h-1.75M6.364 17.636l-1.061 1.061m12.728-12.728l-1.061 1.061" />
    </svg>
    <div className="text-lg font-semibold text-[#1A2B5C] mb-1">No Data Available</div>
    <p className="text-sm text-gray-500 max-w-sm">
      No MISC user data found for the selected filters. Try adjusting your search criteria.
    </p>
  </div>
);

const rankColors = ['#FFD700', '#C0C0C0', '#CD7F32', '#64748b', '#64748b'];

const Top5MiscUsers = ({ filters }) => {
  const [topUsers, setTopUsers] = useState([]);
  const [focus, setFocus] = useState(false);
  const { accessToken } = useAuth();

  useEffect(() => {
    const fetchTopUsers = async () => {
      try {
        const filteredParams = {};
        Object.keys(filters || {}).forEach((key) => {
          if (filters[key]) filteredParams[key] = filters[key];
        });

        const baseUrl = getRoundRobinBaseUrl();
        const response = await api.get(
          `${baseUrl}/superadminDashboard/getSuperAdminMiscCount`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: filteredParams,
          }
        );

        const users = Array.isArray(response.data?.data?.currentMonthData)
          ? response.data.data.currentMonthData
          : [];

        setTopUsers(users.slice(0, 5));
      } catch (error) {
        console.error('Error fetching top users:', error);
        setTopUsers([]);
      }
    };

    fetchTopUsers();
  }, [accessToken, filters]);

  const content = (
    <>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-extrabold uppercase tracking-wide text-[#1A2B5C] flex items-center gap-2">
          Top 5 MISC Users
        </h2>
        <button
          onClick={() => setFocus(true)}
          className="text-xs px-3 py-1 rounded-md border border-[#1A2B5C] text-[#1A2B5C] hover:bg-[#1A2B5C] hover:text-white transition"
        >
          Focus Mode
        </button>
      </div>

      <div className="min-h-[280px] space-y-3">
        {topUsers.length > 0 ? (
          topUsers.map((user, index) => (
            <div
              key={user.employeeId || index}
              className="relative group flex items-center justify-between px-5 py-3 bg-white rounded-xl border border-gray-100 shadow-md hover:shadow-lg transition-transform transform hover:scale-[1.01] animate-fadeInUp"
              title={`${user.employeeName} • ${user.TotalHrs} hrs`}
            >
              {/* Glow strip */}
              <div className="absolute -bottom-1 left-4 right-4 h-1 bg-gradient-to-r from-[#EE1D23] to-[#1A2B5C] rounded-full opacity-20 blur-md" />

              <div className="flex items-center gap-3">
                <span
                  className="text-sm font-bold w-6 h-6 flex items-center justify-center rounded-full text-white"
                  style={{ backgroundColor: rankColors[index] }}
                >
                  {index + 1}
                </span>
                <FaUserCircle className="text-2xl text-[#1A2B5C] group-hover:scale-105 transition-transform" />
                <div className="flex flex-col">
                  <span className="font-medium text-sm text-[#1A2B5C]">{user.employeeName}</span>
                  <span className="text-xs text-gray-500">Emp ID: {user.employeeId}</span>
                </div>
              </div>

              <span className="font-semibold text-[#EE1D23] text-base group-hover:text-[#1A2B5C] transition-colors">
                {user.TotalHrs}
              </span>
            </div>
          ))
        ) : (
          <NoDataMessage />
        )}
      </div>
    </>
  );

  return (
    <>
      {!focus && <CardWrapper>{content}</CardWrapper>}

      {focus && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4">
          <CardWrapper>
            <button
              onClick={() => setFocus(false)}
              className="absolute top-5 right-5 bg-white border border-gray-200 shadow-md rounded-full p-2 text-gray-600 hover:text-[#EE1D23]"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
            {content}
          </CardWrapper>
        </div>
      )}
    </>
  );
};

export default Top5MiscUsers;
